import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { LoginCard } from "./LoginCard";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ delay, duration: 1, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 50]);

  return (
    <section id="hero" ref={ref} data-testid="hero-section" className="relative overflow-hidden">
      <div aria-hidden className="pointer-events-none absolute -left-40 -top-40 h-[36rem] w-[36rem] rounded-full bg-sky-200/50 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -right-40 top-48 h-[30rem] w-[30rem] rounded-full bg-amber-100/70 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-6 pb-28 pt-32 sm:px-10 lg:px-14 lg:pt-36">
        <div className="grid items-start gap-16 lg:grid-cols-12">
          <motion.div style={{ y: textY }} className="lg:col-span-7">
            <motion.span
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05, duration: 0.7, ease: EASE }}
              className="inline-block rounded-full border border-amber-200 bg-amber-100 px-4 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-amber-900"
              data-testid="hero-badge"
            >
              Jyoti Academy · OTET Paper 1 &amp; 2
            </motion.span>

            <h1 className="mt-8 font-odia text-5xl font-bold leading-[1.15] tracking-tight text-slate-900 sm:text-6xl lg:text-7xl">
              <MaskedLine delay={0.15}>ତମସୋ ମା ଜ୍ୟୋର୍ତିଗମୟ ।</MaskedLine>
              <MaskedLine delay={0.3} className="mt-3 font-display text-3xl italic text-sky-700 sm:text-4xl lg:text-5xl">From darkness, lead me to light.</MaskedLine>
            </h1>

            <div className="mt-8">
              <MaskedLine delay={0.5} className="font-odia text-2xl font-medium tracking-wide text-amber-700 sm:text-3xl lg:text-4xl">
                ସା ବିଦ୍ୟା ଯା ବିମୁକ୍ତୟେ ।
              </MaskedLine>
            </div>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75, duration: 0.8, ease: EASE }}
              className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
              data-testid="hero-description"
            >
              &ldquo;Knowledge is that which liberates.&rdquo; Structured coaching for OTET
              Paper 1 &amp; 2 — weekly plans, mentor guidance and test series that help
              you all the way to qualification.
            </motion.p>

            <motion.div
              initial={{ clipPath: "inset(18% 10% 18% 10% round 28px)", opacity: 0 }}
              animate={{ clipPath: "inset(0% 0% 0% 0% round 28px)", opacity: 1 }}
              transition={{ delay: 0.55, duration: 1.2, ease: EASE }}
              className="relative mt-14 max-w-md"
              data-testid="saraswati-frame"
            >
              <div aria-hidden className="absolute -inset-6 rounded-[2.5rem] bg-gradient-to-b from-amber-200/60 to-sky-200/50 blur-2xl" />
              <div className="relative overflow-hidden rounded-[1.75rem] border-4 border-amber-200/80 bg-white shadow-2xl">
                <motion.img
                  style={{ y: imgY }}
                  src="/assets/saraswati.jpg"
                  alt="Goddess Saraswati — symbol of divine knowledge"
                  className="h-[26rem] w-full scale-110 object-cover object-top"
                  data-testid="saraswati-image"
                />
                <div className="absolute inset-x-0 bottom-0 bg-slate-900/85 p-5 backdrop-blur-sm">
                  <p className="font-odia text-lg text-amber-200">ସା ବିଦ୍ୟା ଯା ବିମୁକ୍ତୟେ ।</p>
                  <p className="mt-1 text-xs text-slate-200">Goddess Saraswati — the divine light of learning</p>
                </div>
              </div>
            </motion.div>
          </motion.div>

          <div className="lg:col-span-5" id="login">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 1, ease: EASE }}
              className="lg:sticky lg:top-28"
            >
              <LoginCard />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
